import React from 'react';
import { History, Plus } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import type { ChatSession } from '@/api/client';

interface ChatHistoryDropdownProps {
  sessions: ChatSession[];
  currentSessionId: string | null;
  loading: boolean;
  showHistory: boolean;
  setShowHistory: (show: boolean) => void;
  historyRef: React.RefObject<HTMLDivElement | null>;
  onLoadSession: (sessionId: string) => void;
  onNewChat: () => void;
  formatSessionDate: (dateString: string) => string;
  /** Disable session switching (e.g. while a response is streaming) */
  disabled?: boolean;
}

export function ChatHistoryDropdown({
  sessions,
  currentSessionId,
  loading,
  showHistory,
  setShowHistory,
  historyRef,
  onLoadSession,
  onNewChat,
  formatSessionDate,
  disabled = false,
}: ChatHistoryDropdownProps) {
  const activeSessions = sessions.filter(s => !s.deleted_at);

  return (
    <div className="flex items-center gap-1 relative" ref={historyRef}>
      <Button
        variant="ghost"
        size="icon"
        className="h-8 w-8"
        onClick={() => setShowHistory(!showHistory)}
        title="Chat history"
        disabled={disabled}
      >
        <History className="h-4 w-4" />
      </Button>
      <Button
        variant="ghost"
        size="icon"
        className="h-8 w-8"
        onClick={onNewChat}
        title="New chat"
        disabled={disabled}
      >
        <Plus className="h-4 w-4" />
      </Button>

      {showHistory && (
        <div className="absolute right-0 top-full mt-1 w-64 max-w-[calc(100vw-2rem)] bg-popover border border-border rounded-lg shadow-lg z-50 max-h-80 overflow-y-auto">
          <div className="p-2 border-b border-border">
            <p className="text-xs font-medium text-muted-foreground">Chat History</p>
          </div>
          <div className="p-1">
            {loading ? (
              <div className="p-3 text-center text-xs text-muted-foreground">Loading...</div>
            ) : activeSessions.length === 0 ? (
              <div className="p-3 text-center text-xs text-muted-foreground">No previous chats</div>
            ) : (
              activeSessions.map((session) => (
                <button
                  key={session.id}
                  onClick={() => !disabled && onLoadSession(session.id)}
                  disabled={disabled}
                  className={cn(
                    "w-full text-left p-2 rounded-md text-sm hover:bg-muted/50 transition-colors",
                    currentSessionId === session.id && "bg-muted"
                  )}
                >
                  <p className="truncate text-xs font-medium">{session.title}</p>
                  <p className="text-xs text-muted-foreground mt-0.5">{formatSessionDate(session.updated_at)}</p>
                </button>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
}
